(() => {
  'use strict';

  const controllers = new WeakMap();

  function activateMedia(root, mediaId, focus = false) {
    if (!mediaId) return;
    root.querySelectorAll('[data-product-media]').forEach((media) => {
      const active = String(media.dataset.mediaId) === String(mediaId);
      media.classList.toggle('is-active', active);
      media.hidden = !active;
      media.setAttribute('aria-hidden', String(!active));
      if (!active) {
        media.querySelectorAll('video').forEach((video) => video.pause());
      }
    });
    root.querySelectorAll('[data-media-target]').forEach((thumbnail) => {
      const active = String(thumbnail.dataset.mediaTarget) === String(mediaId);
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', String(active));
      if (active && focus) thumbnail.focus({ preventScroll: true });
    });
  }

  function selectedOptionValueIds(root) {
    return Array.from(root.querySelectorAll('[data-option-value]:checked'))
      .map((input) => input.dataset.optionValueId)
      .filter(Boolean);
  }

  function updateSelectedLabels(root) {
    root.querySelectorAll('[data-option-position]').forEach((group) => {
      const checked = group.querySelector('[data-option-value]:checked');
      const label = group.querySelector('[data-selected-option-value]');
      if (checked && label) {
        const target = group.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
        label.textContent = target?.textContent.trim() || checked.value;
      }
    });
  }

  function syncFeaturedProduct(root, incoming) {
    const variantId = incoming.dataset.selectedVariantId || '';
    root.dataset.selectedVariantId = variantId;
    root.querySelectorAll('[data-variant-id-input], [data-installment-variant-id]').forEach((input) => {
      input.value = variantId;
    });

    const incomingPrice = incoming.querySelector('[data-product-price]');
    const currentPrice = root.querySelector('[data-product-price]');
    if (incomingPrice && currentPrice) currentPrice.innerHTML = incomingPrice.innerHTML;

    const incomingButton = incoming.querySelector('[data-add-to-cart]');
    root.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      button.disabled = incomingButton?.disabled ?? true;
      const currentText = button.querySelector('[data-add-to-cart-text]');
      const incomingText = incomingButton?.querySelector('[data-add-to-cart-text]');
      if (currentText && incomingText) currentText.textContent = incomingText.textContent.trim();
    });

    const activeMedia = incoming.querySelector('[data-product-media].is-active');
    if (activeMedia) activateMedia(root, activeMedia.dataset.mediaId);
    window.Shopify?.PaymentButton?.init?.();
  }

  async function updateVariant(root, changedInput) {
    const optionIds = selectedOptionValueIds(root);
    if (!optionIds.length) return;
    const controller = controllers.get(root);
    controller?.abort();
    const nextController = new AbortController();
    controllers.set(root, nextController);

    root.classList.add('is-updating');
    const context = root.dataset.productContext;
    const productUrl = changedInput?.dataset.productUrl || root.dataset.productUrl || window.location.pathname;
    const sectionId = context === 'featured' ? 'main-product' : root.dataset.sectionId;
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('option_values', optionIds.join(','));
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), {
        signal: nextController.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Unable to update this option');
      const html = await response.text();
      const incomingDocument = new DOMParser().parseFromString(html, 'text/html');
      const incomingRoot = incomingDocument.querySelector('[data-product-root]');
      if (!incomingRoot) throw new Error('This option combination is unavailable');

      if (context === 'featured') {
        syncFeaturedProduct(root, incomingRoot);
        root.classList.remove('is-updating');
      } else {
        const selectedVariantId = incomingRoot.dataset.selectedVariantId;
        root.replaceWith(incomingRoot);
        initProductRoot(incomingRoot);
        window.JamairoTheme?.bind(incomingRoot);
        window.Shopify?.PaymentButton?.init?.();
        if (root.dataset.updateUrl === 'true') {
          const browserUrl = new URL(productUrl, window.location.origin);
          browserUrl.searchParams.delete('section_id');
          browserUrl.searchParams.delete('option_values');
          if (selectedVariantId) browserUrl.searchParams.set('variant', selectedVariantId);
          history.replaceState({}, '', browserUrl);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      root.classList.remove('is-updating');
      window.JamairoTheme?.announce(error.message);
    }
  }

  function openImageZoom(button) {
    const media = button.closest('[data-product-media]');
    const image = media?.querySelector('img');
    if (!image) return;
    const dialog = document.createElement('dialog');
    dialog.className = 'product-zoom modal';
    const closeButton = document.createElement('button');
    closeButton.className = 'modal__close';
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', document.documentElement.lang === 'nl' ? 'Sluiten' : 'Close');
    closeButton.textContent = '×';
    const zoomedImage = document.createElement('img');
    zoomedImage.src = image.currentSrc || image.src;
    zoomedImage.alt = image.alt;
    dialog.append(closeButton, zoomedImage);
    document.body.appendChild(dialog);
    const close = () => {
      dialog.close();
      dialog.remove();
      button.focus();
    };
    closeButton.addEventListener('click', close);
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.showModal();
    closeButton.focus();
  }

  async function shareProduct(button) {
    const data = {
      title: button.dataset.shareTitle || document.title,
      url: button.dataset.shareUrl || window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(data.url);
        const status = button.parentElement.querySelector('[data-share-status]');
        const success = button.dataset.shareSuccess || (document.documentElement.lang === 'nl' ? 'Link gekopieerd' : 'Link copied');
        if (status) status.textContent = success;
        window.JamairoTheme?.announce(success);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        window.JamairoTheme?.announce(button.dataset.shareError || error.message);
      }
    }
  }

  function bindStickyAtc(root) {
    const sticky = root.querySelector('[data-sticky-atc]');
    const mainButton = root.querySelector('[data-product-form-wrapper] [data-add-to-cart]');
    if (!sticky || !mainButton) return;
    sticky.setAttribute('aria-hidden', 'true');
    sticky.inert = true;
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.inert = !visible;
      },
      { threshold: 0 }
    );
    observer.observe(mainButton);
  }

  function initProductRoot(root) {
    if (!root || root.dataset.productBound === 'true') return;
    root.dataset.productBound = 'true';
    root.addEventListener('change', (event) => {
      const sellingPlan = event.target.closest('[data-selling-plan-select]');
      if (sellingPlan) {
        root.querySelectorAll('[data-selling-plan-input]').forEach((input) => {
          input.value = sellingPlan.value;
        });
        const selectedPrice = sellingPlan.selectedOptions[0]?.dataset.price;
        const currentPrice = root.querySelector('[data-product-price] .price__current');
        if (selectedPrice && currentPrice) currentPrice.textContent = selectedPrice;
        return;
      }
      const quantity = event.target.closest('[data-quantity-input]');
      if (quantity) {
        const stickyQuantity = root.querySelector('[data-sticky-quantity]');
        if (stickyQuantity) stickyQuantity.value = quantity.value;
      }
      const option = event.target.closest('[data-option-value]');
      if (!option) return;
      updateSelectedLabels(root);
      updateVariant(root, option);
    });
    root.addEventListener('click', (event) => {
      const thumbnail = event.target.closest('[data-media-target]');
      if (thumbnail) {
        activateMedia(root, thumbnail.dataset.mediaTarget, true);
        return;
      }
      const zoom = event.target.closest('[data-media-zoom]');
      if (zoom) {
        openImageZoom(zoom);
        return;
      }
      const share = event.target.closest('[data-share-button]');
      if (share) shareProduct(share);
    });
    bindStickyAtc(root);
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-product-root]').forEach(initProductRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
